import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from '../ToolRenderers';

// Message interface for the MetricNamesToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Metric data structure
interface Metric {
    name: string;
}

interface MetricNamesResponse {
    metrics: string[];
}

// Tool renderer for metric names data
export function MetricNamesToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // We only want to render the response, not the call
    if (!message.tool_call_response_content) {
        // This is a tool call, do the default rendering
        return <DefaultToolCallRenderer message={message} />;
    }

    let response: MetricNamesResponse | null = null;
    try {
        response = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing metric names response data:', error);
        return null;
    }

    // Validate response
    if (!response || !response.metrics || !Array.isArray(response.metrics)) {
        return null;
    }

    // Convert the string array to an array of objects for the DataTable
    const metricData = response.metrics.map(metric => ({ name: metric }));

    // Define columns for the DataTable
    const columns: ColumnDef<Metric>[] = [
        {
            id: "name",
            accessorKey: "name",
            header: "Metric Name",
            cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
        }
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    return (
        <div className="w-full border rounded">
            <DataTable
                columns={columns}
                data={metricData}
                tableTitle="Available Metrics"
                filters={filters}
                showPagination={true}
                removeBorder={true}
                textFilteringColumnName="name"
                textFilteringPlaceholder="Search metric names..."
                columnConfigs={[
                    { id: "name", isFilterable: true }
                ]}
            />
        </div>
    );
}

// Static method to determine if this renderer should render a message
MetricNamesToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};